import type { EditorHost } from '@blocksuite/std';
import type { ChatAgentCommand, ChatAgentOps } from '../types/chat-message.d';
import {
  type BlockModel,
  type BlockSnapshot,
  type DocSnapshot,
  Slice,
  type Store,
} from '@blocksuite/affine/store';
import { getContentFromSlice } from '../../../blocksuite/utils/markdown-utils';

// 断言 chat 给出的参数格式
export function assertValidChatCommand(
  params: ChatAgentCommand<ChatAgentOps>
): boolean {
  if (params.uuid && Array.isArray(params.command)) {
    return true;
  }

  return false;
}

export async function convertSingleBlockToMarkdown(
  host: EditorHost,
  blockId: string
): Promise<string> {
  try {
    // 1. 获取指定的block
    const block = host.std.store.getBlock(blockId);
    if (!block) {
      throw new Error(`Block ${blockId} not found`);
    }

    const slice = Slice.fromModels(host.std.store, [block.model]);

    // 转换为 markdown
    const selectionMarkdown = await getContentFromSlice(
      host,
      slice,
      'markdown'
    );

    return selectionMarkdown;
  } catch (error) {
    console.error('Error converting block to markdown:', error);
    throw error;
  }
}

// 纯函数 获取所有的 block
export function getAllBlocks(model: Store) {
  const result: BlockModel[] = [];
  function traverse(block: BlockModel) {
    result.push(block);
    if (block.children && block.children.length > 0) {
      block.children.forEach(traverse);
    }
  }
  if (model.root) {
    traverse(model.root);
  }
  return result;
}

// 纯函数 获取所有的 block
export function getAllBlocksBySnapshot(snapshot: DocSnapshot) {
  const result: BlockSnapshot[] = [];
  function traverseChildren(block: BlockSnapshot) {
    result.push(block);
    if (block.children && block.children.length > 0) {
      block.children.forEach(traverseChildren);
    }
  }
  if (snapshot.blocks) {
    traverseChildren(snapshot.blocks);
  }
  return result;
}

export function removeBlockFromSnapshot(
  snapshot: DocSnapshot,
  blockIds: string[]
): DocSnapshot {
  const result: DocSnapshot = JSON.parse(JSON.stringify(snapshot));

  function traverseFilter(block: BlockSnapshot) {
    if (block.children && block.children.length > 0) {
      // block.children.forEach(traverseFilter);
      block.children = block.children.filter(
        item => !blockIds.includes(item.id)
      );
      block.children.forEach(traverseFilter);
    }
  }

  traverseFilter(result.blocks);

  return result;
}

export function getIndexSnapshotByBlockId(
  snapshot: DocSnapshot,
  parentId: string,
  childId: string
): number {
  let result: number = -1;

  function traverseFilter(block: BlockSnapshot) {
    if (block.id === parentId) {
      result = block.children.findIndex(item => item.id === childId);
      return;
    }

    if (block.children && block.children.length > 0) {
      block.children.forEach(traverseFilter);
    }
  }

  traverseFilter(snapshot.blocks);

  return result;
}

export function removeBeforeFirstLineBreak(str: string): string {
  // 找到第一个换行符的位置
  const firstLineBreakIndex = str.indexOf('\n');

  // 如果没有找到换行符，直接返回原字符串
  if (firstLineBreakIndex === -1) {
    return str;
  }

  // 返回第一个换行符之后的所有内容
  return str.substring(firstLineBreakIndex + 2);
}

type SplitMarkdownPart = {
  type: 'text' | 'image';
  value: string;
};

/**
 * 将 markdown 字符串拆分为文本和图片部分的数组
 * 支持多个图片，按顺序返回所有文本和图片片段
 */
export function splitMarkdown(md: string): SplitMarkdownPart[] {
  // 匹配 ![alt](url) 图片语法（全局匹配）
  const regex = /!\[[^\]]*\]\([^)]+\)/g;
  const parts: SplitMarkdownPart[] = [];

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(md)) !== null) {
    // 添加图片前的文本（如果有）
    const textBefore = md.slice(lastIndex, match.index).trim();
    if (textBefore) {
      parts.push({ type: 'text', value: textBefore });
    }

    // 添加图片
    parts.push({ type: 'image', value: match[0].trim() });

    lastIndex = match.index + match[0].length;
  }

  // 添加最后一个图片后的文本（如果有）
  const textAfter = md.slice(lastIndex).trim();
  if (textAfter) {
    parts.push({ type: 'text', value: textAfter });
  }

  // 如果没有匹配到任何图片，返回整个文本
  if (parts.length === 0 && md.trim()) {
    parts.push({ type: 'text', value: md.trim() });
  }

  return parts;
}

export function markdownHasImage(md: string): boolean {
  const regex = /!\[[^\]]*\]\([^)]+\)/;

  const match = md.match(regex);

  if (!match || match.index === undefined) {
    return false;
  }

  return true;
}
